import React, { useContext } from 'react';

/** @jsx jsx */
import { css, jsx } from '@emotion/core';

import { Page } from '../Components/Page';
import { welcomeImage } from '../Components/WelcomeImage';
import { Code } from '../Components/CodePreview/Code';
import { darkModeContext } from '../darkModeContext';

export const WelcomePage: React.FC = () => {
  const docsDarkMode = useContext(darkModeContext);
  const docsDarkModeValue = docsDarkMode.value || false;

  return (
    <Page title="">
      <div
        css={css`
          text-align: center;
        `}
      >
        <div
          css={css`
            margin-bottom: 40px;
          `}
        >
          <tag3-text type="h1" colour-scheme={docsDarkModeValue ? 'dark' : 'light'}>
            Welcome to tag-components!
          </tag3-text>
        </div>
        <img
          css={css`
            width: 500px;
            @media (max-width: 768px) {
              width: 170px;
            }
          `}
          src={welcomeImage}
          alt=""
        />
      </div>

      <div
        css={css`
          text-align: center;
          font-weight: bold;
          font-style: italic;
          margin-bottom: 40px;
        `}
      >
        Rapidly build consistent and robust apps
      </div>
      <p
        css={css`
          margin-bottom: 30px;
        `}
      >
        tag-components is a library of <b>web components</b> that can be used in any HTML based app.
        They have the <b>access UX</b> baked in so they help us quickly build our apps with a
        consistent look and feel.
      </p>
      <tag3-text type="h2" colour-scheme={docsDarkModeValue ? 'dark' : 'light'}>
        Installation
      </tag3-text>
      <p>
        First add the following link to the tag-component styles in the <code>head</code> of the
        page:
      </p>
      <Code
        code={`v3
<link href='https://tagcomponents.accessacloud.com/{version}/tag-library-v3/tag-library-v3.css' rel='stylesheet'>

v1
<link href='https://tagcomponents.accessacloud.com/[version]/tag-library.css' rel='stylesheet'>`}
      ></Code>

      <p>
        The following tag-components <code>script</code> then needs to be placed in the{' '}
        <code>body</code> of the page:
      </p>
      <Code
        code={`v3
<script src="https://tagcomponents.accessacloud.com/{version}/tag-library-v3/tag-library-v3.esm.js" type="module"></script>
<script src="https://tagcomponents.accessacloud.com/{version}/tag-library-v3/tag-library-v3.js" nomodule></script>

v1
<script src='https://tagcomponents.accessacloud.com/{version}/tag-library.js'></script>`}
      ></Code>
      <p>
        Substitute <code>version</code> for the version of tag-components you are targeting.
      </p>
      <p>
        There are 1st class <b>React</b> versions of all the tag-components. So, if you are
        developing in React, checkout{' '}
        <a href="https://confluence.accessnet.co.uk/display/TC/React+Wrappers+v2">
          this confluence page
        </a>{' '}
        and{' '}
        <a href="https://confluence.accessnet.co.uk/pages/viewpage.action?pageId=54427724">
          this tutorial
        </a>{' '}
        to learn how to install and consume the React tag-components.
      </p>

      <p>
        If you want to target the version of tag-components currently in development, use the
        following:
      </p>
      <Code
        code={`<script src='https://tagcomponents-dev.accessacloud.com/latest/tag-library.js'></script>`}
      ></Code>

      <p>
        Version 3 of tag-components is being developed. If you want to target this, use the
        following:
      </p>
      <Code
        code={`<script src="https://tagcomponents-dev.accessacloud.com/dev/v3/tag-library-v3.esm.js" type="module"></script>
<script src="https://tagcomponents-dev.accessacloud.com/dev/v3/tag-library-v3.js" nomodule></script>
<link href='https://tagcomponents-dev.accessacloud.com/dev/v3/tag-library-v3.css' rel='stylesheet'>`}
      ></Code>

      <div
        css={css`
          margin: 40px 0 0px;
        `}
      >
        <tag3-text type="h2" colour-scheme={docsDarkModeValue ? 'dark' : 'light'}>
          Usage
        </tag3-text>
      </div>

      <p>Here's the HTML to add a button:</p>
      <Code code={`<tag-button accent="atoll">Hi, I'm a button in HTML</tag-button>`}></Code>

      <p>Here's the JSX to add a button:</p>
      <Code code={`<TagButton accent="atoll" text="Hi, I'm a button in JSX"></TagButton>`}></Code>

      <div
        css={css`
          margin: 40px 0 0px;
        `}
      >
        <tag3-text type="h2" colour-scheme={docsDarkModeValue ? 'dark' : 'light'}>
          Useful links
        </tag3-text>
      </div>

      <ul>
        <li>
          The tag-component{' '}
          <a href="https://dev.azure.com/access-devops/Access%20Tag%20Components/_wiki/wikis/Access-Tag-Components.wiki/74822/tag-components">
            azure wiki
          </a>{' '}
          contains lots of useful information including tutorials and best practice
        </li>
        <li>
          If you have an idea or an issue with tag-components then you can log this in the{' '}
          <a href="https://dev.azure.com/access-devops/Access%20Workspace/_boards/board/t/Prometheus%20Squad/Stories">
            tag-component azure board
          </a>
          {'. '}
        </li>
        <li>
          If you want to ask a question about tag-components or help other devs with their
          consumption of tag-components then join the teams forum{' '}
          <a href="https://teams.microsoft.com/l/channel/19%3Ad6671164aff549f09c8548c43f73bb18%40thread.tacv2/Tag%20Component%20Forum?groupId=11747135-a331-43a3-a333-0f70f5733932">
            Tag Component Forum
          </a>
        </li>
      </ul>
    </Page>
  );
};
